import React from 'react';
import { createPortal } from 'react-dom';

export interface ActionDropdownAction {
  key: string;
  label: string;
  icon: string;
  onClick: () => void | Promise<void>;
  className?: string;
  separateAfter?: boolean;
  isLoading?: boolean;
}

interface ActionDropdownProps {
  /** The record/data object this dropdown is for */
  record: any;
  /** Whether the dropdown is currently open */
  isOpen: boolean;
  /** Function to toggle the dropdown state */
  onToggle: () => void;
  /** Array of actions to display in the dropdown */
  actions: ActionDropdownAction[];
  /** Optional aria label for accessibility */
  ariaLabel?: string;
  /** Optional className for the button */
  buttonClassName?: string;
  /** Optional icon class for the trigger button */
  triggerIcon?: string;
}

const ActionDropdown: React.FC<ActionDropdownProps> = ({
  record,
  isOpen,
  onToggle,
  actions,
  ariaLabel,
  buttonClassName = 'p-1 hover:bg-gray-100 rounded focus:outline-none focus:ring-2 focus:ring-blue-500',
  triggerIcon = 'ri-more-2-line text-gray-500 text-lg',
}) => {
  const [dropdownPosition, setDropdownPosition] = React.useState<{
    top?: number;
    bottom?: number;
    left?: number;
    right?: number;
  }>({});
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  // Calculate optimal dropdown position relative to viewport
  React.useEffect(() => {
    if (isOpen && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const dropdownWidth = 160;
      const dropdownHeight = Math.max(100, actions.length * 40 + 16); // Dynamic height based on actions

      const spaceBelow = viewportHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;
      const spaceRight = viewportWidth - buttonRect.right;
      const spaceLeft = buttonRect.left;

      let position: typeof dropdownPosition = {};

      // Vertical positioning
      if (spaceBelow >= dropdownHeight) {
        position.top = buttonRect.bottom + 4;
      } else if (spaceAbove >= dropdownHeight) {
        position.bottom = viewportHeight - buttonRect.top + 4;
      } else {
        position.top = Math.max(
          4,
          Math.min(buttonRect.bottom + 4, viewportHeight - dropdownHeight - 4)
        );
      }

      // Horizontal positioning
      if (spaceRight >= dropdownWidth) {
        position.right = viewportWidth - buttonRect.right;
      } else if (spaceLeft >= dropdownWidth) {
        position.left = buttonRect.left - dropdownWidth;
      } else {
        position.right = viewportWidth - buttonRect.right;
      }

      setDropdownPosition(position);
    }
  }, [isOpen, actions.length]);

  const handleActionClick = async (action: ActionDropdownAction) => {
    if (isLoading) return;

    setIsLoading(true);

    try {
      await action.onClick();
    } catch (error) {
      console.error(`Error executing action ${action.key}:`, error);
    } finally {
      setIsLoading(false);
    }
  };

  const defaultAriaLabel =
    ariaLabel || `Actions for ${record?.name || record?.platNomor || 'item'}`;

  return (
    <>
      <div className="sticky right-0 bg-white border-l border-gray-200 relative">
        <button
          ref={buttonRef}
          className={buttonClassName}
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              onToggle();
            }
            if (e.key === 'Escape' && isOpen) {
              e.stopPropagation();
              onToggle();
            }
          }}
          aria-label={defaultAriaLabel}
          aria-expanded={isOpen}
          aria-haspopup="menu"
        >
          <i className={triggerIcon}></i>
        </button>
      </div>

      {/* Portal-rendered dropdown with fixed positioning */}
      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            className="fixed min-w-40 bg-white border border-gray-200 rounded-md shadow-lg z-[99999]"
            style={dropdownPosition}
            onClick={(e) => e.stopPropagation()}
            role="menu"
            aria-label="Action menu"
          >
            {actions.map((action, index) => {
              const isActionLoading = isLoading || action.isLoading;
              return (
                <React.Fragment key={action.key}>
                  <button
                    className={`w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center focus:outline-none focus:bg-gray-100 transition-colors duration-150 ${action.className || ''
                      } ${action.separateAfter ? 'border-b border-gray-100' : ''} ${isActionLoading ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    onClick={() => {
                      if (!isActionLoading) {
                        handleActionClick(action);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (
                        (e.key === 'Enter' || e.key === ' ') &&
                        !isActionLoading
                      ) {
                        e.preventDefault();
                        handleActionClick(action);
                      }
                    }}
                    disabled={isActionLoading}
                    role="menuitem"
                  >
                    {isActionLoading ? (
                      <div className="mr-2 w-4 h-4 flex items-center justify-center">
                        <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin opacity-60" />
                      </div>
                    ) : (
                      <i className={`${action.icon} mr-2`}></i>
                    )}
                    {action.label}
                  </button>
                </React.Fragment>
              );
            })}
          </div>,
          document.body
        )}
    </>
  );
};

export default ActionDropdown;
